import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  CheckCircle2,
  CircleAlert,
  FileText,
  Loader2,
  LockKeyhole,
  Maximize2,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  X,
} from 'lucide-react';
import SessionReauthDialog from './SessionReauthDialog';
import {
  getEnterpriseCertificationDocumentPreview,
  submitEnterpriseCertification,
  uploadEnterpriseCertificationDocument,
} from './corporateFinanceApi';

const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;
const PREVIEW_LOAD_TIMEOUT_MS = 15000;
const DOCUMENT_TYPES = new Set(['image/jpeg', 'image/png', 'application/pdf']);
const CREDIT_CODE_PATTERN = /^[0-9A-HJ-NPQRTUWXY]{18}$/;
const NUMERIC_STATUS = {
  0: 'pending',
  1: 'approved',
  2: 'rejected',
  3: 'revoked',
  4: 'superseded',
};

const emptyForm = user => ({
  company_name: '',
  unified_social_credit_code: '',
  registered_address: '',
  registered_phone: '',
  contact_name: user?.username || '',
  contact_phone: '',
  contact_email: user?.email || '',
  authorization_confirmed: false,
});

const certificationForm = (certification, user) => ({
  ...emptyForm(user),
  company_name: certification?.company_name || '',
  unified_social_credit_code: certification?.unified_social_credit_code || '',
  registered_address: certification?.registered_address || '',
  registered_phone: certification?.registered_phone || '',
  contact_name: certification?.contact_name || user?.username || '',
  contact_phone: certification?.contact_phone || '',
  contact_email: certification?.contact_email || user?.email || '',
});

export const normalizeEnterpriseCertificationStatus = certification => {
  if (!certification) return 'none';
  const rawStatus = certification.status;
  return NUMERIC_STATUS[rawStatus] || String(rawStatus || 'none').toLowerCase();
};
const dateText = value => value ? new Date(value).toLocaleString('zh-CN') : '—';

function statusTone(status) {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'pending';
  if (['rejected', 'revoked'].includes(status)) return 'danger';
  return 'neutral';
}

function documentError(file) {
  if (!DOCUMENT_TYPES.has(file?.type)) return '仅支持 JPG、PNG 或 PDF 文件';
  if (file.size > MAX_DOCUMENT_SIZE) return '文件大小不能超过 10 MB';
  return '';
}

function certificationDocument(certification, documentType) {
  return Array.isArray(certification?.documents)
    ? certification.documents.find(item => item.document_type === documentType)
    : null;
}

function materialMime(material) {
  const mime = String(material?.mime_type || material?.mime || '').toLowerCase();
  if (mime) return mime;
  const path = String(material?.preview_url || '').split(/[?#]/, 1)[0].toLowerCase();
  if (/\.(jpe?g|png)$/.test(path)) return 'image/*';
  if (/\.pdf$/.test(path)) return 'application/pdf';
  return '';
}

function CertificationDocumentPreview({ preview, onClose, onRetry, darkActive }) {
  const closeButtonRef = useRef(null);
  const [contentState, setContentState] = useState('loading');
  const { material, label, returnFocus } = preview;
  const mime = materialMime(material);

  useEffect(() => {
    if (contentState !== 'loading') return undefined;
    const timeoutId = window.setTimeout(() => setContentState('error'), PREVIEW_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [contentState, material.preview_url]);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    const handleDialogKeys = event => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = closeButtonRef.current?.closest('[role="dialog"]');
      const focusable = Array.from(dialog?.querySelectorAll('button:not(:disabled), a[href]') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && window.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && window.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.document.addEventListener('keydown', handleDialogKeys);
    return () => {
      window.document.removeEventListener('keydown', handleDialogKeys);
      window.document.body.style.overflow = previousOverflow;
      returnFocus?.focus?.();
    };
  }, [onClose, returnFocus]);

  return createPortal(
    <div className={`enterprise-document-preview${darkActive ? ' is-dark' : ''}`} onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="enterprise-document-preview-title">
        <header>
          <div>
            <h2 id="enterprise-document-preview-title">{label}预览</h2>
            <span>{material.original_name || '已上传认证材料'}</span>
          </div>
          <button ref={closeButtonRef} type="button" aria-label="关闭材料预览" onClick={onClose}><X size={16} /></button>
        </header>
        <div className="enterprise-document-preview__body">
          {contentState === 'loading' && (
            <div className="enterprise-document-preview__state" role="status">
              <Loader2 className="canvas-vnext-spin" size={18} />
              <span>正在加载{label}…</span>
            </div>
          )}
          {contentState === 'error' && (
            <div className="enterprise-document-preview__state is-error" role="alert">
              <CircleAlert size={22} />
              <strong>材料暂时无法在线预览</strong>
              <span>文件加载失败或临时授权已失效，请重新加载。</span>
              <button type="button" onClick={onRetry}>重新加载</button>
            </div>
          )}
          {mime.startsWith('image/') ? (
            <img
              className={contentState === 'loaded' ? '' : 'is-loading'}
              src={material.preview_url}
              alt={`${label}预览`}
              onLoad={() => setContentState('loaded')}
              onError={() => setContentState('error')}
            />
          ) : (
            <iframe
              className={contentState === 'loaded' ? '' : 'is-loading'}
              src={material.preview_url}
              title={`${label}预览`}
              tabIndex="-1"
              referrerPolicy="no-referrer"
              onLoad={() => setContentState('loaded')}
              onError={() => setContentState('error')}
            />
          )}
        </div>
        <footer>
          <span>材料长期私有保存，本页仅用于在线查看。</span>
          {contentState !== 'error' && <button type="button" onClick={onRetry}>重新加载</button>}
        </footer>
      </section>
    </div>,
    window.document.body,
  );
}

function CertificationDocumentField({
  label,
  required,
  material,
  existing,
  reusable,
  reuseBlockedText,
  uploading,
  disabled,
  onUpload,
  onPreview,
  previewing,
  previewDisabled,
}) {
  const canPreview = Boolean(material?.id);
  return (
    <article className={`enterprise-certification-upload-item${material ? ' is-uploaded' : ''}`}>
      {canPreview ? (
        <button
          type="button"
          className="enterprise-certification-upload-thumb"
          aria-label={previewing ? `正在授权查看${label}` : `放大查看${label}`}
          aria-busy={previewing}
          disabled={previewDisabled}
          onClick={event => onPreview(material, label, event.currentTarget)}
        >
          <FileText size={22} />
          <span>{previewing ? <><Loader2 className="canvas-vnext-spin" size={11} />授权中</> : <><Maximize2 size={11} />放大</>}</span>
        </button>
      ) : (
        <div className="enterprise-certification-upload-thumb is-empty" aria-hidden="true"><UploadCloud size={20} /></div>
      )}
      <span className="enterprise-certification-upload-meta">
        <strong>{label}{required ? '（必传）' : '（选填）'}</strong>
        <small>{material
          ? `${existing ? (reusable ? '已上传，可继续使用' : reuseBlockedText) : '本次新上传'} · ${material.original_name || '认证材料'}`
          : (required ? 'JPG、PNG、PDF，最大 10 MB' : '经办人需要时可补充上传')}</small>
      </span>
      <label className="enterprise-certification-upload-action">
        <input type="file" accept="image/jpeg,image/png,application/pdf" aria-label={`${material ? '替换' : '上传'}${label}`} disabled={disabled} onChange={onUpload} />
        {uploading ? <Loader2 className="canvas-vnext-spin" size={13} /> : <UploadCloud size={13} />}
        <span>{uploading ? '上传中' : material ? '替换' : '上传'}</span>
      </label>
    </article>
  );
}

function ReadOnlyCertification({ certification, status, onPreview, previewingDocumentId }) {
  const businessLicense = certificationDocument(certification, 'business_license');
  const authorization = certificationDocument(certification, 'authorization');
  const previewBusy = Boolean(previewingDocumentId);
  const documentButton = (material, label) => {
    if (!material?.id) return null;
    const previewing = Boolean(material.id) && String(material.id) === previewingDocumentId;
    return (
      <button
        type="button"
        aria-busy={previewing}
        disabled={previewBusy}
        onClick={event => onPreview(material, label, event.currentTarget)}
      >
        {previewing ? <Loader2 className="canvas-vnext-spin" size={12} /> : <FileText size={12} />}
        {previewing ? `正在授权查看${label}` : `查看${label}`}
      </button>
    );
  };
  return (
    <div className="enterprise-certification-summary">
      <div className="enterprise-certification-summary__identity">
        <span><Building2 size={15} />认证企业</span>
        <strong>{certification.company_name}</strong>
        <code>{certification.unified_social_credit_code}</code>
      </div>
      <dl>
        <div><dt>联系人</dt><dd>{certification.contact_name || '—'} · {certification.contact_phone || certification.contact_email || '—'}</dd></div>
        <div><dt>注册地址</dt><dd>{certification.registered_address || '未填写'}</dd></div>
        <div><dt>提交时间</dt><dd>{dateText(certification.created_at)}</dd></div>
        <div><dt>{status === 'approved' ? '通过时间' : '审核状态'}</dt><dd>{status === 'approved' ? dateText(certification.reviewed_at) : certification.status_label}</dd></div>
      </dl>
      <div className="enterprise-certification-documents">
        {documentButton(businessLicense, '营业执照')}
        {documentButton(authorization, '授权证明')}
      </div>
    </div>
  );
}

export default function EnterpriseCertificationPanel({
  scope,
  user,
  certification,
  permissions = {},
  loading,
  error,
  onRetry,
  onChanged,
  onEditingChange,
  preview = false,
  darkActive = false,
}) {
  const status = normalizeEnterpriseCertificationStatus(certification);
  const canSubmit = Boolean(permissions.can_submit) && !preview;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => emptyForm(user));
  const [documents, setDocuments] = useState({ business_license: null, authorization: null });
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [reauthOpen, setReauthOpen] = useState(false);
  const [retryPayload, setRetryPayload] = useState(null);
  const [materialPreview, setMaterialPreview] = useState(null);
  const [previewingDocumentId, setPreviewingDocumentId] = useState('');
  const previewRequestRef = useRef(null);
  const certificationIdentity = `${certification?.id ?? ''}:${certification?.version ?? ''}:${status}`;
  const userDefaultsRef = useRef({ username: user?.username || '', email: user?.email || '' });
  userDefaultsRef.current = { username: user?.username || '', email: user?.email || '' };
  const formDefaults = useMemo(() => certificationForm({
    company_name: certification?.company_name,
    unified_social_credit_code: certification?.unified_social_credit_code,
    registered_address: certification?.registered_address,
    registered_phone: certification?.registered_phone,
    contact_name: certification?.contact_name,
    contact_phone: certification?.contact_phone,
    contact_email: certification?.contact_email,
  }), [
    certification?.company_name,
    certification?.contact_email,
    certification?.contact_name,
    certification?.contact_phone,
    certification?.registered_address,
    certification?.registered_phone,
    certification?.unified_social_credit_code,
  ]);

  useEffect(() => {
    setForm({
      ...formDefaults,
      contact_name: formDefaults.contact_name || userDefaultsRef.current.username,
      contact_email: formDefaults.contact_email || userDefaultsRef.current.email,
    });
    setDocuments({ business_license: null, authorization: null });
    setEditing(['none', 'rejected', 'revoked'].includes(status));
    onEditingChange?.(false);
    setFeedback({ type: '', message: '' });
    setRetryPayload(null);
    setReauthOpen(false);
    setMaterialPreview(null);
    previewRequestRef.current = null;
    setPreviewingDocumentId('');
  }, [certificationIdentity, formDefaults, onEditingChange, scope, status]);

  useEffect(() => {
    setForm(current => ({
      ...current,
      contact_name: current.contact_name || user?.username || '',
      contact_email: current.contact_email || user?.email || '',
    }));
  }, [user?.email, user?.username]);

  const updateField = event => {
    const { name, type, checked, value } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setFeedback({ type: '', message: '' });
  };

  const openMaterialPreview = useCallback(async (material, label, returnFocus) => {
    if (!material?.id || previewRequestRef.current) return;

    const requestToken = Symbol('enterprise-document-preview');
    previewRequestRef.current = requestToken;
    setPreviewingDocumentId(String(material.id));
    setFeedback({ type: '', message: '' });
    try {
      const response = await getEnterpriseCertificationDocumentPreview(scope, material.id);
      if (previewRequestRef.current !== requestToken) return;
      const freshMaterial = response?.document || response;
      if (!freshMaterial?.preview_url) throw new Error('系统未返回可用的预览地址，请重试');
      setMaterialPreview({ material: { ...material, ...freshMaterial }, label, returnFocus });
    } catch (previewError) {
      if (previewRequestRef.current !== requestToken) return;
      setFeedback({ type: 'error', message: getErrorMessage(`${label}查看失败：${previewError.message || '暂时无法授权'}，请重试`) });
    } finally {
      if (previewRequestRef.current === requestToken) {
        previewRequestRef.current = null;
        setPreviewingDocumentId('');
      }
    }
  }, [scope]);

  const closeMaterialPreview = useCallback(() => setMaterialPreview(null), []);
  const retryMaterialPreview = useCallback(() => {
    if (!materialPreview) return;
    const { material, label, returnFocus } = materialPreview;
    setMaterialPreview(null);
    openMaterialPreview(material, label, returnFocus);
  }, [materialPreview, openMaterialPreview]);

  const uploadDocument = async (event, documentType) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = documentError(file);
    if (validation) {
      setFeedback({ type: 'error', message: getErrorMessage(validation) });
      return;
    }
    setUploading(documentType);
    setFeedback({ type: '', message: '' });
    try {
      const document = await uploadEnterpriseCertificationDocument(file, documentType);
      setDocuments(current => ({ ...current, [documentType]: document }));
      setFeedback({ type: 'success', message: documentType === 'business_license' ? '营业执照已上传' : '授权证明已上传' });
    } catch (uploadError) {
      setFeedback({ type: 'error', message: getErrorMessage(uploadError, '文件上传失败，请重试') });
    } finally {
      setUploading('');
    }
  };

  const runSubmit = async payload => {
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      await submitEnterpriseCertification(payload);
      setRetryPayload(null);
      setEditing(false);
      onEditingChange?.(false);
      setFeedback({ type: 'success', message: '认证资料已提交，审核期间企业开票将暂停' });
      await onChanged?.();
    } catch (submitError) {
      if (submitError.code === 'REAUTH_REQUIRED') {
        setRetryPayload(payload);
        setReauthOpen(true);
        setFeedback({ type: 'warning', message: '请先验证身份，验证成功后会自动继续提交' });
      } else {
        setFeedback({ type: 'error', message: getErrorMessage(submitError, '认证资料提交失败') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = event => {
    event.preventDefault();
    const companyName = form.company_name.trim();
    const creditCode = form.unified_social_credit_code.trim().toUpperCase();
    if (!companyName || !form.registered_address.trim()
      || !form.contact_name.trim() || !form.contact_phone.trim() || !form.contact_email.trim()) {
      setFeedback({ type: 'error', message: '请完整填写企业名称、注册地址和联系人资料' });
      return;
    }
    if (!CREDIT_CODE_PATTERN.test(creditCode)) {
      setFeedback({ type: 'error', message: '请输入正确的 18 位统一社会信用代码' });
      return;
    }
    const identityChanged = Boolean(certification) && (
      companyName !== String(certification.company_name || '').trim()
      || creditCode !== String(certification.unified_social_credit_code || '').trim().toUpperCase()
    );
    const existingBusinessLicense = certificationDocument(certification, 'business_license');
    const existingAuthorization = certificationDocument(certification, 'authorization');
    const businessLicense = documents.business_license || (!identityChanged ? existingBusinessLicense : null);
    const authorization = documents.authorization || (!identityChanged ? existingAuthorization : null);
    if (!businessLicense?.id) {
      setFeedback({
        type: 'error',
        message: getErrorMessage(identityChanged ? '企业名称或统一社会信用代码已变更，请上传对应的新营业执照' : '请上传本次认证使用的营业执照', '请上传本次认证使用的营业执照'),
      });
      return;
    }
    if (!form.authorization_confirmed) {
      setFeedback({ type: 'error', message: '请确认你有权代表该企业提交认证资料' });
      return;
    }
    runSubmit({
      scope,
      company_name: companyName,
      unified_social_credit_code: creditCode,
      business_license_document_id: businessLicense.id,
      authorization_document_id: authorization?.id || null,
      registered_address: form.registered_address.trim(),
      registered_phone: form.registered_phone.trim() || null,
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
      contact_email: form.contact_email.trim(),
      authorization_confirmed: true,
    });
  };

  const handleReauthenticated = async () => {
    setReauthOpen(false);
    if (retryPayload) await runSubmit(retryPayload);
  };

  if (loading) {
    return <section className="enterprise-certification-card is-loading" aria-live="polite"><Loader2 className="canvas-vnext-spin" size={17} /><span>正在核验企业认证状态</span></section>;
  }

  if (error) {
    return <section className="enterprise-certification-card is-error" role="alert"><CircleAlert size={17} /><div><strong>企业认证状态加载失败</strong><span>{error}</span></div><button type="button" onClick={onRetry}><RefreshCw size={12} />重新加载</button></section>;
  }

  const showForm = editing && canSubmit;
  const locked = status === 'pending';
  const approved = status === 'approved';
  const existingBusinessLicense = certificationDocument(certification, 'business_license');
  const existingAuthorization = certificationDocument(certification, 'authorization');
  const identityChanged = Boolean(certification) && (
    form.company_name.trim() !== String(certification.company_name || '').trim()
    || form.unified_social_credit_code.trim().toUpperCase()
      !== String(certification.unified_social_credit_code || '').trim().toUpperCase()
  );
  const businessLicense = documents.business_license || existingBusinessLicense;
  const authorization = documents.authorization || existingAuthorization;
  const existingBusinessLicenseReusable = !identityChanged && Boolean(existingBusinessLicense?.id);
  const existingAuthorizationReusable = !identityChanged && Boolean(existingAuthorization?.id);
  const reuseBlockedText = identityChanged
    ? '原认证材料，仅供查看；主体变更后需替换'
    : '历史材料仅供查看；请重新上传后提交';

  return (
    <section className={`enterprise-certification-card is-${status}`} aria-labelledby="enterprise-certification-title">
      <header>
        <div className="enterprise-certification-step">1</div>
        <div>
          <h2 id="enterprise-certification-title">企业认证</h2>
          <p>企业抬头和统一社会信用代码审核通过后锁定使用，不能在开票时临时修改。</p>
        </div>
        <span className={`enterprise-certification-status is-${statusTone(status)}`}>
          {approved ? <CheckCircle2 size={12} /> : locked ? <Loader2 size={12} /> : <ShieldCheck size={12} />}
          {certification?.status_label || (status === 'none' ? '未认证' : status)}
        </span>
      </header>

      <p className="enterprise-certification-disclaimer">
        平台仅核验所提交材料在审核时点的一致性，不代表市场监管、税务机关或第三方权威企业实名认证。
      </p>

      {!canSubmit && !approved && (
        <div className="enterprise-certification-permission" role="status">
          <LockKeyhole size={15} />
          <span>{scope === 'team' ? '当前成员无权提交团队企业认证，请联系团队所有者或管理员。' : '当前账号暂不能提交企业认证。'}</span>
        </div>
      )}

      {certification && !showForm && (
        <ReadOnlyCertification
          certification={certification}
          status={status}
          onPreview={openMaterialPreview}
          previewingDocumentId={previewingDocumentId}
        />
      )}

      {certification?.review_remark && ['rejected', 'revoked'].includes(status) && (
        <p className="enterprise-certification-review" role="alert"><CircleAlert size={13} />审核说明：{certification.review_remark}</p>
      )}

      {approved && !editing && canSubmit && (
        <div className="enterprise-certification-actions">
          <span><LockKeyhole size={13} />企业名称与信用代码已锁定用于开票</span>
          <button type="button" onClick={() => { setEditing(true); onEditingChange?.(true); setFeedback({ type: 'warning', message: '修改资料提交后，企业开票会暂停至复核通过' }); }}>修改资料</button>
        </div>
      )}

      {showForm && (
        <form className="enterprise-certification-form" onSubmit={submit}>
          {approved && <p className="enterprise-certification-change-warning"><CircleAlert size={13} />{existingBusinessLicense?.id ? '已上传营业执照可继续使用；企业名称或信用代码变更时请替换。' : '历史营业执照可查看，但缺少可复用标识，请替换上传。'}提交即暂停企业开票，待复核通过后恢复。</p>}
          <div className="enterprise-certification-fields two">
            <label><span>企业法定全称</span><input name="company_name" value={form.company_name} maxLength={100} onChange={updateField} placeholder="请与营业执照完全一致" /></label>
            <label><span>统一社会信用代码</span><input name="unified_social_credit_code" value={form.unified_social_credit_code} maxLength={18} onChange={event => setForm(current => ({ ...current, unified_social_credit_code: event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '') }))} placeholder="18 位统一社会信用代码" /></label>
            <label><span>注册地址</span><input name="registered_address" value={form.registered_address} maxLength={100} onChange={updateField} placeholder="请与营业执照登记信息一致" /></label>
            <label><span>注册电话（选填）</span><input name="registered_phone" value={form.registered_phone} maxLength={64} onChange={updateField} /></label>
            <label><span>联系人</span><input name="contact_name" value={form.contact_name} maxLength={64} onChange={updateField} /></label>
            <label><span>联系电话</span><input name="contact_phone" type="tel" value={form.contact_phone} maxLength={32} onChange={updateField} /></label>
            <label><span>联系邮箱</span><input name="contact_email" type="email" value={form.contact_email} maxLength={100} onChange={updateField} /></label>
          </div>

          <div className="enterprise-certification-uploads">
            <CertificationDocumentField
              label="营业执照"
              required
              material={businessLicense}
              existing={!documents.business_license && Boolean(existingBusinessLicense)}
              reusable={existingBusinessLicenseReusable}
              reuseBlockedText={reuseBlockedText}
              uploading={uploading === 'business_license'}
              disabled={Boolean(uploading) || submitting}
              onUpload={event => uploadDocument(event, 'business_license')}
              onPreview={openMaterialPreview}
              previewing={Boolean(businessLicense?.id) && String(businessLicense.id) === previewingDocumentId}
              previewDisabled={Boolean(previewingDocumentId)}
            />
            <CertificationDocumentField
              label="授权证明"
              material={authorization}
              existing={!documents.authorization && Boolean(existingAuthorization)}
              reusable={existingAuthorizationReusable}
              reuseBlockedText={reuseBlockedText}
              uploading={uploading === 'authorization'}
              disabled={Boolean(uploading) || submitting}
              onUpload={event => uploadDocument(event, 'authorization')}
              onPreview={openMaterialPreview}
              previewing={Boolean(authorization?.id) && String(authorization.id) === previewingDocumentId}
              previewDisabled={Boolean(previewingDocumentId)}
            />
          </div>

          <label className="enterprise-certification-confirm">
            <input name="authorization_confirmed" type="checkbox" checked={form.authorization_confirmed} onChange={updateField} />
            <span>我确认上述资料真实有效，并有权代表该企业提交认证和开票申请。</span>
          </label>

          {feedback.message && <p className={`enterprise-certification-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.message}</p>}

          <footer>
            <span>资料仅用于企业认证、开票及必要审计。</span>
            <div>
              {approved && <button type="button" className="is-quiet" onClick={() => { setEditing(false); onEditingChange?.(false); setFeedback({ type: '', message: '' }); }}>取消修改</button>}
              <button type="submit" disabled={submitting || Boolean(uploading)}>{submitting ? <><Loader2 className="canvas-vnext-spin" size={13} />正在提交…</> : '提交企业认证'}</button>
            </div>
          </footer>
        </form>
      )}

      {!showForm && feedback.message && <p className={`enterprise-certification-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.message}</p>}

      {reauthOpen && (
        <SessionReauthDialog
          loginMethods={user?.login_methods}
          onClose={() => setReauthOpen(false)}
          onVerified={handleReauthenticated}
          darkActive={darkActive}
        />
      )}

      {materialPreview && (
        <CertificationDocumentPreview
          preview={materialPreview}
          onClose={closeMaterialPreview}
          onRetry={retryMaterialPreview}
          darkActive={darkActive || Boolean(window.document.querySelector('.dark .corporate-finance-panel'))}
        />
      )}
    </section>
  );
}
